import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, ShieldX, Download, FileText, CheckCircle2, XCircle, Check, ArrowLeft, ArrowRight, X } from "lucide-react";

/* -------------------------------------------------------------------------- */
/* Constants                                                                   */
/* -------------------------------------------------------------------------- */

const DOCUMENT_STEPS = [
  "Upload Document",
  "Automatic Verification",
  "Tamper Detection",
  "Policy & Key Validation",
  "Verification Result",
];

const ARTIFACT_STEPS = [
  "Upload Artifact",
  "Automatic Verification",
  "Artifact Integrity Analysis",
  "Policy & Key Validation",
  "Verification Result",
];

const STEP_CIRCLE_CLASS = {
  done: "bg-green-500 border-green-500 text-white",
  current: "bg-blue-600 border-blue-600 text-white",
  upcoming: "bg-white border-gray-300 text-gray-500",
};

const STEP_LABEL_CLASS = {
  done: "text-green-600",
  current: "text-blue-600",
  upcoming: "text-gray-500",
};

const SCORE_TEXT_CLASS = {
  good: "text-green-600",
  fair: "text-yellow-600",
  poor: "text-red-600",
};

const SCORE_BAR_CLASS = {
  good: "bg-green-500",
  fair: "bg-yellow-500",
  poor: "bg-red-500",
};

const IMAGE_FILE_REGEX = /\.(png|jpg|jpeg|gif|bmp|webp)$/i;

/* -------------------------------------------------------------------------- */
/* Pure helpers                                                                */
/* -------------------------------------------------------------------------- */

const isNil = (value) => value === null || value === undefined;

// "Not Enabled" for null/undefined (not applicable), otherwise pass/fail label
function statusLabel(val, passLabel, failLabel) {
  if (isNil(val)) return "Not Enabled";
  return val ? passLabel : failLabel;
}

function getStepStatus(index, currentStep) {
  if (index < currentStep) return "done";
  if (index === currentStep) return "current";
  return "upcoming";
}

// Spec section 17: a clean result shows "AI confidence score"; suspicious /
// tamper results show "Risk score" (same figure the AI Tamper Detection page
// displayed).
function getScoreLabel(isCleanResult, isArtifact) {
  if (!isCleanResult) return "Risk Score";
  return isArtifact ? "Integrity Score" : "AI Confidence Score";
}

// For a risk score a HIGH number is bad, so callers flip it before applying
// the green/yellow/red thresholds.
function getScoreTone(goodness) {
  if (goodness >= 80) return "good";
  if (goodness >= 50) return "fair";
  return "poor";
}

// The TSA timestamp comes back from the backend as raw ISO 8601 UTC
// (e.g. "2026-08-27T06:32:49Z"). Render it in the user's local time,
// human-readable, while keeping the raw value as a fallback.
function formatTimestamp(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "medium",
  });
}

function blockchainLabel(result, isArtifact) {
  if (isArtifact && result.blockchain_valid === null) return "Not Enabled";
  return result.blockchain_valid ? "Verified" : "Failed";
}

function buildArtifactDetails(result) {
  return [
    { label: "Trust Level", value: result.trust_level || "N/A" },
    { label: "Detected Format", value: result.format || "N/A" },
  ];
}

function buildDetails({ result, file, isArtifact, noun, scoreLabel, scoreValue, isCleanResult }) {
  return [
    { label: "Overall Status", value: result.document_valid ? "Valid" : "Invalid", badge: true },
    { label: scoreLabel, value: `${scoreValue}%`, progress: scoreValue, invert: !isCleanResult },
    { label: "Verification Time", value: new Date().toLocaleString() },
    { label: `${noun} Name`, value: file?.name || "N/A" },
    { label: "File Size", value: file?.size || "N/A" },
    { label: "Signature Type", value: result.algorithm || result.sign_mode || "Unknown" },
    { label: "Timestamp Verification", value: statusLabel(result.tsa_valid, "Valid", "Failed") },
    { label: "Blockchain Verification", value: blockchainLabel(result, isArtifact) },
    { label: isArtifact ? "Signature Validation" : "PQ Signature", value: result.pq_signature_valid ? "Verified" : "Failed" },
    { label: "Certificate Status", value: statusLabel(result.certificate_valid, "Valid", "Invalid") },
    { label: isArtifact ? "Hash Integrity" : "Merkle Integrity", value: result.merkle_valid ? "Verified" : "Failed" },
    { label: `${noun} Hash`, value: result.cid || "N/A" },
    { label: "IPFS CID", value: result.original_ipfs_cid || "N/A" },
    { label: "Timestamp", value: formatTimestamp(result.timestamp) || "N/A" },
    ...(isArtifact ? buildArtifactDetails(result) : []),
  ];
}

/* -------------------------------------------------------------------------- */
/* Presentational components                                                   */
/* -------------------------------------------------------------------------- */

function StepProgress({ steps, currentStep }) {
  return (
    <div className="flex items-center justify-between mb-2 h-[72px]">
      {steps.map((step, index) => {
        const status = getStepStatus(index, currentStep);
        const isLast = index === steps.length - 1;

        return (
          <div key={step} className="flex-1 flex flex-col items-center relative">
            {!isLast && (
              <div className={`absolute top-5 left-1/2 w-full h-[2px] ${status === "done" ? "bg-green-500" : "bg-gray-300"}`} />
            )}
            <div className={`z-10 w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${STEP_CIRCLE_CLASS[status]}`}>
              {status === "done" ? <Check size={12} /> : index + 1}
            </div>
            <p className={`mt-1 text-sm font-medium text-center hidden sm:block ${STEP_LABEL_CLASS[status]}`}>{step}</p>
          </div>
        );
      })}
    </div>
  );
}

function ResultBanner({ valid, noun }) {
  const StatusIcon = valid ? CheckCircle2 : XCircle;
  const ShieldIcon = valid ? ShieldCheck : ShieldX;
  const tone = valid ? "text-green-600" : "text-red-600";

  return (
    <div className="flex flex-col items-center text-center mb-3">
      <div className="relative">
        <ShieldIcon size={80} className={tone} />
        <StatusIcon size={30} className={`absolute -right-2 bottom-0 ${tone} bg-white rounded-full`} />
      </div>
      <h2 className={`text-[24px] font-bold mt-2 ${valid ? "text-gray-800" : "text-red-700"}`}>
        {valid ? `${noun} Is Authentic` : `${noun} Verification Failed`}
      </h2>
      <p className="text-gray-500 mt-1 text-16px">
        {valid ? `The ${noun.toLowerCase()} is valid, unchanged and verified.` : "One or more verification checks failed."}
      </p>
    </div>
  );
}

function ScoreValue({ item }) {
  const goodness = item.invert ? 100 - item.progress : item.progress;
  const tone = getScoreTone(goodness);

  return (
    <div className="flex items-center gap-3">
      <p className={`text-sm font-semibold ${SCORE_TEXT_CLASS[tone]}`}>{item.value}</p>
      <div className="w-24 bg-gray-200 rounded-full h-2">
        <div className={`h-2 rounded-full ${SCORE_BAR_CLASS[tone]}`} style={{ width: `${item.progress}%` }} />
      </div>
    </div>
  );
}

function DetailValue({ item }) {
  if (item.badge) {
    const badgeClass = item.value === "Valid" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700";
    return <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${badgeClass}`}>{item.value}</span>;
  }
  if (typeof item.progress === "number") return <ScoreValue item={item} />;
  return <p className="text-sm font-semibold text-gray-800 text-right break-all max-w-xs">{item.value}</p>;
}

function DetailsGrid({ details }) {
  return (
    <div className="max-w-3xl mx-auto space-y-3">
      {details.map((item) => (
        <div key={item.label} className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-2 bg-gray-50">
          <p className="text-16px text-gray-500 font-medium">{item.label}</p>
          <DetailValue item={item} />
        </div>
      ))}
    </div>
  );
}

function Placeholder({ message, children }) {
  return (
    <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-3">
      <FileText size={48} />
      <p className="text-sm">{message}</p>
      {children}
    </div>
  );
}

function FilePreview({ file }) {
  if (!file?.url) return <Placeholder message="No document available" />;

  if (file.name?.toLowerCase().endsWith(".pdf")) {
    return (
      <iframe
        src={`${file.url}#pagemode=none&toolbar=0`}
        title="Document Preview"
        className="w-full rounded"
        style={{ minHeight: "70vh" }}
      />
    );
  }

  if (IMAGE_FILE_REGEX.test(file.name)) {
    return <img src={file.url} alt={file.name} className="max-w-full rounded shadow" />;
  }

  return (
    <Placeholder message="Preview not available">
      <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline">
        Open in new tab
      </a>
    </Placeholder>
  );
}

function ViewFileModal({ file, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col"
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <p className="font-semibold text-gray-800 text-sm truncate">{file?.name || "Document"}</p>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-auto bg-gray-100 flex justify-center p-4">
          <FilePreview file={file} />
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Page component                                                              */
/* -------------------------------------------------------------------------- */

export default function VerificationResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentStep = 4;

  const verificationResult = location.state?.verificationResult || {};
  const uploadedFiles = location.state?.uploadedFiles || [];
  const file = uploadedFiles[0];
  const aiScore = location.state?.aiScore || 0;
  const riskScore = location.state?.riskScore ?? 0;
  const classification = location.state?.classification;

  // Artifact verification reuses this wizard; the adapter sets kind: "artifact".
  const isArtifact = verificationResult.kind === "artifact";
  const noun = isArtifact ? "Artifact" : "Document";
  const steps = isArtifact ? ARTIFACT_STEPS : DOCUMENT_STEPS;
  const valid = Boolean(verificationResult.document_valid);

  const isCleanResult = classification ? classification === "clean" : true;
  const scoreLabel = getScoreLabel(isCleanResult, isArtifact);
  const scoreValue = isCleanResult ? aiScore : riskScore;

  const [viewOpen, setViewOpen] = useState(false);

  const details = buildDetails({
    result: verificationResult,
    file,
    isArtifact,
    noun,
    scoreLabel,
    scoreValue,
    isCleanResult,
  });

  const wizardState = { uploadedFiles, aiScore, riskScore, classification, verificationResult };
  const goToReport = () => navigate("/verification-report", { state: wizardState });
  const goBack = () => navigate("/policy-key-validation", { state: wizardState });

  return (
    <div className="w-full bg-white mx-auto">
      <StepProgress steps={steps} currentStep={currentStep} />

      {/* RESULT CARD */}
      <div className={`border-2 rounded-2xl p-6 mb-6 ${valid ? "border-green-200" : "border-red-200"}`}>
        <ResultBanner valid={valid} noun={noun} />
        <DetailsGrid details={details} />
      </div>

      {/* ACTION BUTTONS */}
      <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mt-6 mb-4">
        <button
          onClick={goToReport}
          className="flex items-center gap-2 px-4 py-2 border-2 border-blue-200 rounded-xl text-blue-700 font-semibold hover:bg-blue-50 transition text-14px"
        >
          <Download size={18} /> Download Verification Report
        </button>
        <button
          onClick={() => setViewOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition text-14px"
        >
          <FileText size={18} /> View {noun}
        </button>
      </div>

      {/* FOOTER */}
      <div className="flex justify-between mt-3">
        <button
          onClick={goBack}
          className="inline-flex items-center gap-1.5 h-[44px] px-6 bg-gray-100 text-gray-600 rounded-[10px] hover:bg-gray-200 transition text-sm font-medium"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <button
          onClick={goToReport}
          className="flex items-center gap-1.5 h-[44px] px-6 rounded-[10px] text-white text-sm font-medium bg-[#2563EB] hover:bg-[#1D4ED8] transition"
        >
          Next <ArrowRight size={14} />
        </button>
      </div>

      {viewOpen && <ViewFileModal file={file} onClose={() => setViewOpen(false)} />}
    </div>
  );
}